<script setup lang="ts">
import { provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { parsePhoneNumber } from "libphonenumber-js";

import { ModalName } from '@/constants/modals';
import { ROUTES } from '@/constants/routes';
import { formatFullName } from "@/helpers/name";
import { isset } from "@/helpers/object";
import missionsService from "@/modules/missions/services/missionsService";
import { useMissionActionsStore } from "@/store/missionActionsStore";
import { type IMissionDB, MissionCommand, type WorkerMission } from '@/types/mission';

import type { MissionAlreadyConfirmed, WorkerAlreadyConfirmedModalProps } from "@/components/Mission/MissionSideModal/types";
import type { DropdownGeneratorProps, DropdownProps } from "@/dsComponents/Dropdown/types";
import type { IWorkerSelection } from "@/modules/missions/types";

import Button from "@/dsComponents/buttons/Button.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownGenerator from "@/dsComponents/Dropdown/DropdownGenerator.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import SideModal2 from "@/dsComponents/modals/SideModal2.vue";

import WorkerAlreadyConfirmedModal from "@/components/Mission/MissionSideModal/WorkerAlreadyConfirmedModal.vue";
import MissionWorkersSelection from "@/modules/missions/components/matching/MissionWorkersSelection.vue";

interface WorkersSelectionModalProps {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    mission: any;
    open: boolean;
}

const props = defineProps<WorkersSelectionModalProps>();

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const emit = defineEmits<{ (e: "close"): void; (e: "update", value: any): void }>();

const { t } = useI18n();
const modalStore = useModalStore();
const toaster = useToast();

const workersSelection = ref<IWorkerSelection[]>([]);

const workerAlreadyConfirmedId = ref();
const workerAlreadyConfirmedParams = ref<WorkerAlreadyConfirmedModalProps>();

const missionActionsStore = useMissionActionsStore();
const actions = ref<DropdownGeneratorProps>({ actions: [] });

const dropdownActionsProps: DropdownProps = {
    mode: "hover",
    width: "fit-content",
    styles: { color: "#494E61", "font-size": "12px" },
};

const formatWorkers = (workers: WorkerMission[]): IWorkerSelection[] => {
    return workers.map((worker: WorkerMission) => {
        return {
            status: worker.status,
            workerId: worker.workerId,
            lastname: worker.lastname || "",
            firstname: worker.firstname || "",
            availableCommands: worker.availableCommands || [],
            phone: worker.phone ? parsePhoneNumber(worker.phone, "FR").formatNational() : undefined,
        };
    });
};
const workerActionLoading = ref(false);
provide("actionLoading", workerActionLoading);

const handleVerifyConfirmWorker = async (workerId: string) => {
    try {
        const result = await missionsService.verifyWorkerConfirmMission({
            missionId: props.mission.id,
            workerId: workerId,
        });

        if (result.length > 0) {
            const currentWorker = workersSelection.value.find((worker) => worker.workerId === workerId);
            if (currentWorker) {
                workerAlreadyConfirmedId.value = currentWorker.workerId;
                workerAlreadyConfirmedParams.value = {
                    isOpen: true,
                    workerName: formatFullName(`${currentWorker.firstname} ${currentWorker.lastname}`),
                    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
                    missionsAlreadyConfirmed: result.map((missionAlreadyConfirmed: any) => ({
                        name: missionAlreadyConfirmed.name,
                        startAt: missionAlreadyConfirmed.startAt,
                        endAt: missionAlreadyConfirmed.endAt,
                    })) as MissionAlreadyConfirmed[],
                };
            }
        } else {
            changeWorkerAction(workerId, MissionCommand.workerConfirm);
        }
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    // biome-ignore lint/correctness/noUnusedVariables: Init Biome
    } catch (error: any) {
        toaster?.open({
            message: t("mission.matching.command.default"),
            type: "error",
            duration: 3000,
        });
    }
};

const changeWorkerAction = async (workerId: string | string[], command: MissionCommand, reason?: string) => {
    const workerIds = Array.isArray(workerId) ? workerId : [workerId];

    if (command === MissionCommand.verifyConfirmWorker) {
        handleVerifyConfirmWorker(workerIds[0]);
        return;
    }

    try {
        if ([MissionCommand.repropose, MissionCommand.propose].includes(command)) workerActionLoading.value = true;
        const result: IMissionDB = await missionsService.sendCommandMission({
            missionId: props.mission.id,
            command,
            data: {
                positionIndex: 0,
                workerId: workerIds,
                reason,
            },
        });

        workersSelection.value = formatWorkers(result.workers || []);
        emit("update", result);
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    } catch (err: any) {
        if (command === MissionCommand.workerConfirm && err?.fullErr?.response?.data[0]?.id === "position.full") {
            modalStore.actions.open(ModalName.TooMuchWorkerConfirm);
            return;
        }

        let errorMessage = t("mission.matching.command.default");

        if (command === MissionCommand.addInterim) {
            errorMessage =
                workerIds.length > 1 ? t("mission.matching.command.addInterim.addManyError") : t("mission.matching.command.addInterim.addError");
        }

        toaster?.open({
            message: errorMessage,
            type: "error",
            duration: 3000,
        });
    } finally {
        workerActionLoading.value = false;
    }
};

const handleClose = () => {
    emit("close");
};

const handleCloseWorkerAlreadyConfirmedModal = () => {
    workerAlreadyConfirmedId.value = undefined;
    workerAlreadyConfirmedParams.value = undefined;
};

const forceWorkerConfirmMission = () => {
    changeWorkerAction(workerAlreadyConfirmedId.value, MissionCommand.workerConfirm);
    handleCloseWorkerAlreadyConfirmedModal();
};

watch(
    () => props.open,
    (newVal) => {
        if (isset(newVal)) {
            workersSelection.value = formatWorkers(props.mission.workers);
            actions.value = {
                relatedData: { missionId: props.mission.id, mission: props.mission },
                actions: missionActionsStore.getMissionActions([...props.mission.availableCommands, MissionCommand.show] as MissionCommand[]),
            };
        }
    },
    { immediate: true }
);
</script>
<template>
    <div class="mission-side-modal">
        <SideModal2 :title="mission?.name" :open="open" :close="false" @close="handleClose" :width="480">
            <template #header-actions>
                <Dropdown v-if="mission.jobOfferId" v-bind="dropdownActionsProps" :debounce="500">
                    <template #trigger-zone>
                        <Button
                            iconOnly="megaphone"
                            type="neutral"
                            variant="ghost"
                            size="xs"
                            :to="{ name: ROUTES.APP.COMPANY_NEED.JOB_OFFERS }"
                        ></Button>
                    </template>
                    <template #dropdown-content>
                        <span>{{ t("page.mission.kanbanCard.jobOffer") }}</span>
                    </template>
                </Dropdown>
                <Dropdown v-if="mission.sendToEvoliaTimestamp" v-bind="dropdownActionsProps" :debounce="500">
                    <template #trigger-zone>
                        <Button iconOnly="cloud-check" iconOnlyColor="#2051F8" type="neutral" variant="ghost" size="xs" />
                    </template>
                    <template #dropdown-content>
                        <span>{{ t("page.mission.kanbanCard.syncWithEvolia") }}</span>
                    </template>
                </Dropdown>
                <Dropdown width="200px" close-on-descendant>
                    <template #trigger-zone>
                        <Button iconOnly="dots" size="xs" type="neutral" variant="ghost" />
                    </template>
                    <template #dropdown-content>
                        <DropdownGenerator v-bind="actions" />
                    </template>
                </Dropdown>
            </template>
            <template #default>
                <MissionWorkersSelection v-if="open" :mission="mission" :workers="workersSelection" @changeWorkerAction="changeWorkerAction" />
            </template>
        </SideModal2>

        <WorkerAlreadyConfirmedModal
            v-if="workerAlreadyConfirmedParams?.isOpen"
            v-bind="workerAlreadyConfirmedParams"
            @cancel="handleCloseWorkerAlreadyConfirmedModal"
            @confirm="forceWorkerConfirmMission"
        />
    </div>
</template>
